import { Popup, type PopupProps } from '@CS/components/Popups';
import { pushEvent } from '@CS/hooks';
import styles from './RulesPopup.module.css';
import BaseButton from '@/components/BaseButton/BaseButton';
import { useRef, useState, type AnimationEventHandler } from 'react';
import CloseIcon from '@CS/assets/close.svg?react';

type RulesPopupMode = 'home' | 'inGame';

type RulesPopupProps = PopupProps & {
  mode?: RulesPopupMode;
  onPrimaryAction?: () => void;
  translations: Record<string, string>;
};

const POPUP_STYLE = {
  backgroundColor: 'transparent',
  height: '100%',
  paddingTop: '5.6rem',
} as const;

const RulesPopup = ({
  mode = 'home',
  onClose,
  onPrimaryAction,
  translations,
  ...props
}: RulesPopupProps) => {
  const buttonLabel = mode === 'inGame' ? translations.rulesPopupresumeButton : translations.startletsGo;
  const title = translations.rulesPopuptitle;
  const [isClosing, setIsClosing] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const pendingCloseActionRef = useRef<(() => void) | null>(null);

  const handleClose = () => {
    setIsClosing(true);
    pendingCloseActionRef.current = onClose ?? null;
  };

  const handleButtonClose = () => {
    pushEvent({
      event: 'button_click',
      eventAction: mode === 'inGame' ? '/fcg26/game_hervatten' : '/fcg26/lets_go',
      eventLabel: '/fcg26/spelregels'
    });
    setIsClosing(true);
    pendingCloseActionRef.current = (onPrimaryAction ?? onClose) ?? null;
  };

  const handleRollAnimationEnd: AnimationEventHandler<HTMLDivElement> = (event) => {
    if (!isClosing) return;
    if (event.target !== event.currentTarget) return;
    if (event.animationName !== 'popup-roll-out-right') return;

    const action = pendingCloseActionRef.current;
    pendingCloseActionRef.current = null;
    action?.();
  };

  return (
    <Popup
      onClose={onClose}
      onBeforeClose={() => setIsClosing(true)}
      hideClose
      allowClickOutside
      className={styles.dialog}
      width="100%"
      opacity={0.6}
      customStyle={POPUP_STYLE}
      {...props}
    >
      <div
        ref={contentRef}
        onAnimationEnd={handleRollAnimationEnd}
        className={[
          styles.popupContent,
          'popup-roll',
          isClosing && 'popup-roll--closing'
        ].filter(Boolean).join(' ')}
      >
        <button
          type="button"
          className={styles.closeButton}
          aria-label="Close"
          onClick={handleClose}
        >
          <CloseIcon />
        </button>
        <div className={styles.body}>
          <h2 className={styles.title}>{title}</h2>
          <ul className={styles.rules}>
            <li>
              <h3 className={styles.ruleTitle}>
                {translations.rulesPopupplayersTitle}
              </h3>
              <p className={styles.ruleDescription}>
                {translations.rulesPopupplayersBody}
              </p>
              <ul className={styles.list}>
                <li>
                  <span className={styles.teamName}>
                    {translations.generalteamTrending}
                  </span>
                  <span>{translations.rulesPopupplayersItemTrending}</span>
                </li>
                <li>
                  <span className={styles.teamName}>
                    {translations.generalteamTeletekst}
                  </span>
                  <span>{translations.rulesPopupplayersItemTeletekst}</span>
                </li>
              </ul>
            </li>

            <li>
              <h3 className={styles.ruleTitle}>
                {translations.rulesPopupsitTitle}
              </h3>
              <p className={styles.ruleDescription}>
                {translations.rulesPopupsitBody}
              </p>
            </li>

            <li>
              <h3 className={styles.ruleTitle}>
                {translations.rulesPopuphowTitle}
              </h3>
              <p className={styles.ruleDescription}>
                {translations.rulesPopuphowBody}
              </p>
            </li>

            <li>
              <h3 className={styles.ruleTitle}>
                {translations.rulesPopuphintTitle}
              </h3>
              <p className={styles.ruleDescription}>
                {translations.rulesPopuphintBody}
              </p>
            </li>
          </ul>
        </div>
        <BaseButton
          className={styles.primaryAction}
          text={buttonLabel}
          onClick={handleButtonClose}
        />
      </div>
    </Popup>
  );
};

export default RulesPopup;